import { Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { $ok } from "foundation-ts/commons";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { WaitingModal } from "@/components/Modals/WaitingModal";
import { AccountTab } from "@containers/userProfile/AccountTab";
import { ConsumptionsTab } from "@containers/userProfile/ConsumptionsTab";
import { SettingsTab } from "@containers/userProfile/SettingsTab";
import { PollingState } from "@/contexts/AuthContext";
import { useAuthContext } from "@/hooks/useAuthContext";
import useQueryParam from "@/hooks/useQueryParams";
import { $g1Color } from "@/theme/G1Style";

import { SubscriptionTab } from "./SubscriptionTab";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";

export const UserProfileView = () => {
    const { connectedUser, pollingState, startPollingUser, stopPollingUser } = useAuthContext();
    const queryCorporationId = useQueryParam("corporationId");
    const mandateState = useQueryParam("mandate");
    const [hiddenSubscriptionTab, setHiddenSubscriptionTab] = useState(true);
    const { onClose } = useDisclosure();
    const toast = useToast();

    const corporation = connectedUser?.corporations?.first();

    const updateUserProfileView = () => {
        setHiddenSubscriptionTab(false);
    }

    useEffect(() => {
        if ($ok(queryCorporationId) && corporation?.apid === queryCorporationId && mandateState === "confirmed") {
            startPollingUser();
        }

        return () => stopPollingUser();
    }, []);

    useEffect(() => {
        if (mandateState !== "confirmed") { return; }

        if (pollingState === PollingState.EXHAUSTED) {
            toast(defaultErrorToast("La validation du mandat prend trop de temps, veuillez recommencer"));
            onClose();
            return;
        }

        if (pollingState !== PollingState.STOPPED) { return; }

        const status = corporation?.mandate?.status;
        if (!!corporation?.mandate?.failedToChange) {
            toast(defaultErrorToast("La validation du nouveau mandat a échoué, veuillez recommencer"));
        } else if (status === "valid") {
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat carte bancaire"));
        } else if (status === "invalid") {
            toast(defaultErrorToast("Votre mandat est invalide, veuillez en définir un nouveau"));
        }
        onClose();
    }, [pollingState]);

    return (
        <PageShellWithThreeColumns
            centralContent={
                <>
                    <ColumnTitle color={$g1Color('global.write')}>
                        <Text>Votre compte</Text>
                    </ColumnTitle>
                    <Tabs isFitted>
                        <TabList color={$g1Color('global.tabs.write')} backgroundColor={$g1Color('global.tabs.bg')}>
                            <Tab fontWeight="semibold">Compte</Tab>
                            <Tab fontWeight="semibold" hidden={hiddenSubscriptionTab}>Abonnement</Tab>
                            <Tab fontWeight="semibold" hidden={hiddenSubscriptionTab}>Mes collaborateurs</Tab>
                            <Tab fontWeight="semibold">Mes consommations</Tab>
                            <Tab fontWeight="semibold">Réglages</Tab>
                        </TabList>
                        <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                        <TabPanels>
                            <TabPanel>
                                <AccountTab updateUserProfileView={updateUserProfileView} />
                            </TabPanel>
                            <TabPanel hidden={hiddenSubscriptionTab}>
                                <SubscriptionTab connectedUser={connectedUser} pricePerExtraUser={60} />
                            </TabPanel>
                            <TabPanel hidden={hiddenSubscriptionTab}></TabPanel>
                            <TabPanel>
                                <ConsumptionsTab />
                            </TabPanel>
                            <TabPanel>
                                <SettingsTab />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                    {pollingState === PollingState.RUNNING && <WaitingModal isOpen={true} onClose={onClose} message="Le mandat que vous souhaitez mettre à jour est encore en attente de validation" />}
                </>
            }
            isOpen={false}
        />
    ) ;
};
